import { FrameworkDef, SemanticRole } from './schema';

// THE framework registry (build-brief §4.1, SAD §2.4, ADR-2). The UI renders
// framework lists, labels and colours purely from here. No `if (framework === …)`
// branching anywhere in components — add a framework by adding a registry entry +
// data, never by editing component logic.
//
// MITRE ATT&CK is the foundational threat taxonomy: every control / countermeasure
// / risk / vulnerability framework maps AGAINST its technique entities.
//
// `populated: false` marks frameworks registered for the rebuild but without source
// data in the repo yet (scaffold decision — build-brief §6, ADR-7: never invent
// mappings). They are schema/UI-supported and slot in once their datasets land.
export const FRAMEWORKS: FrameworkDef[] = [
  {
    id: 'mitre-attack',
    label: 'MITRE ATT&CK',
    shortLabel: 'ATT&CK',
    entityKind: 'offensive-technique',
    semanticRole: 'threat',
    source: 'https://attack.mitre.org/ (Enterprise)',
    version: 'enterprise',
    populated: true,
  },
  {
    id: 'nist-800-53-r5',
    label: 'NIST 800-53 Rev 5',
    shortLabel: 'NIST',
    entityKind: 'control',
    semanticRole: 'defence',
    source: 'NIST SP 800-53 Rev 5',
    version: 'Rev 5',
    populated: true,
  },
  {
    id: 'iso-27001-2022',
    label: 'ISO 27001 (2022)',
    shortLabel: 'ISO',
    entityKind: 'control',
    semanticRole: 'defence',
    source: 'ISO/IEC 27001:2022',
    version: '2022',
    populated: true,
  },
  {
    id: 'cis-v8.1',
    label: 'CIS Controls v8.1 (Coming Soon)',
    shortLabel: 'CIS',
    entityKind: 'control',
    semanticRole: 'defence',
    source: 'CIS Controls v8.1',
    version: '8.1',
    populated: true, // 153 safeguard entities live (Phase 3). ATT&CK linkage still
    // deferred — the CIS community mappings aren't in the repo.
  },
  {
    id: 'd3fend',
    label: 'MITRE D3FEND',
    shortLabel: 'D3FEND',
    entityKind: 'defensive-technique',
    semanticRole: 'defence',
    source: 'https://d3fend.mitre.org/',
    version: '1.x',
    populated: true, // countermeasures + `counters` live (Phase 3). `implemented-by`
    // (D3FEND→NIST 800-53 crosswalk) still deferred — that dataset isn't present.
  },
  {
    id: 'mitre-atlas',
    label: 'MITRE ATLAS',
    shortLabel: 'ATLAS',
    entityKind: 'offensive-technique',
    semanticRole: 'threat',
    source: 'https://atlas.mitre.org/',
    version: 'current',
    populated: false, // TODO: source
  },
  {
    id: 'owasp-llm',
    label: 'OWASP Top 10 for LLMs',
    shortLabel: 'OWASP-LLM',
    entityKind: 'risk',
    semanticRole: 'impact',
    source: 'https://genai.owasp.org/ (Top 10 for LLM Applications)',
    version: '2025',
    populated: false, // TODO: source
  },
  {
    id: 'cisa-kev',
    label: 'CISA KEV',
    shortLabel: 'KEV',
    entityKind: 'vulnerability',
    semanticRole: 'impact',
    source: 'CISA Known Exploited Vulnerabilities Catalog',
    version: 'snapshot 2026-05-30',
    populated: true, // active-exploitation overlay live (Phase 3, snapshot-only)
  },
];

const BY_ID = new Map(FRAMEWORKS.map((f) => [f.id, f]));
const BY_LABEL = new Map(FRAMEWORKS.map((f) => [f.label, f]));

export function getFramework(id: string): FrameworkDef | undefined {
  return BY_ID.get(id);
}

export function getFrameworkByLabel(label: string): FrameworkDef | undefined {
  return BY_LABEL.get(label);
}

/** Control-framework picker source: control-kind frameworks that have content. */
export function getControlFrameworks(opts: { populatedOnly?: boolean } = {}): FrameworkDef[] {
  return FRAMEWORKS.filter(
    (f) => f.entityKind === 'control' && (!opts.populatedOnly || f.populated),
  );
}

/** Tailwind text-colour class for a semantic role (used once components adopt tokens). */
export const ROLE_TEXT_CLASS: Record<SemanticRole, string> = {
  defence: 'text-tv-defence',
  threat: 'text-tv-threat',
  connection: 'text-tv-connection',
  impact: 'text-tv-risk',
};

/** CSS custom-property reference for a role's colour (inline-style dots/markers). */
export const ROLE_VAR: Record<SemanticRole, string> = {
  defence: 'var(--tv-defence)',
  threat: 'var(--tv-threat)',
  connection: 'var(--tv-connection)',
  impact: 'var(--tv-risk)',
};
